<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Payment Methods</h1>
                <v-btn v-if="user.type!=='admin'" color="black" prepend-icon="mdi-plus" @click="showCardDialog">Add
                </v-btn>
            </v-col>
        </v-row>
        <v-row v-if="user.type==='admin'">
            <v-col class="d-flex" cols="12">
                <company-select v-model="selectedCompany" :items="companies"
                          class="me-3" density="compact" label="Search Company"
                          max-width="350" @update:model-value="fetchPMs"
                          clearable/>
                <v-btn :disabled="!selectedCompany" color="black" prepend-icon="mdi-plus" @click="showCardDialog">Add
                </v-btn>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th>Card Holder Name</th>
                        <th>Brand</th>
                        <th>Last Four</th>
                        <th>Default</th>
                        <th v-if="user.type==='admin'">Source</th>
                        <th v-if="user.type==='admin'">IP Address</th>
                        <th v-if="user.type==='admin'">Added</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="card in cards" v-if="cards.length" :key="card.id" class="text-left">
                        <td>{{ card.card_holder_name }}</td>
                        <td>
                            <v-chip size="small" variant="tonal" :color="cardBrandColor(card.brand)">
                                <v-icon start size="14">{{ cardBrandIcon(card.brand) }}</v-icon>
                                {{ strOperations().capitalize(card.brand) }}
                            </v-chip>
                        </td>
                        <td><b>****</b> {{ card.last_four }}</td>
                        <td>
                            <v-icon :icon="card.default?'mdi-check-circle':'mdi-close-circle'"/>
                        </td>
                        <td v-if="user.type==='admin'">
                            <v-chip v-if="card.client_source" size="x-small" :color="card.client_source==='web'?'blue':'green'" variant="tonal">
                                {{ card.client_source }}
                            </v-chip>
                        </td>
                        <td v-if="user.type==='admin'" class="text-caption">{{ card.ip_address || '-' }}</td>
                        <td v-if="user.type==='admin'" class="text-caption">{{ card.created_at ? new Date(card.created_at).toLocaleDateString() : '-' }}</td>
                        <td>
                            <v-btn-group density="compact" divided variant="outlined">
                                <v-btn v-if="!card.default || user.type === 'admin'" class="px-4 text-error" icon="mdi-delete" size="small"
                                       @click="deletePm(card)"/>
                                <v-btn v-if="!card.default" class="px-4" icon="mdi-check" size="small"
                                       @click="setPmDefault(card)"/>
                            </v-btn-group>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="7">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="7">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>
    </v-container>

    <v-dialog v-model="dialogSwitch" persistent scrollable width="500">
        <v-form class="mb-3" validate-on="input lazy" @submit.prevent="submitCard">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Add New Payment Method</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="closeCardDialog"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>
                    <v-row>
                        <v-col cols="12">
                            <v-text-field v-model="formData['name']" :rules="[vRules.required]" autofocus density="compact"
                                          label="Name on Card" variant="plain"/>
                            <div ref="cardElement" class="pa-3"></div>
                        </v-col>
                    </v-row>
                    <div class="trust-section pa-3 rounded-lg mt-1">
                        <div class="d-flex align-center mb-1">
                            <v-icon size="16" color="blue-darken-2" class="mr-2">mdi-shield-check</v-icon>
                            <span class="text-caption font-weight-medium" style="color: #1565C0">
                                Your card details are sent directly to Stripe and never stored on our servers
                            </span>
                        </div>
                        <div class="d-flex align-center mb-1 ml-6">
                            <v-icon size="12" color="grey-darken-1" class="mr-2">mdi-lock</v-icon>
                            <span style="font-size: 11px" class="text-grey-darken-1">256-bit SSL encryption - PCI DSS Level 1 certified</span>
                        </div>
                        <div class="d-flex align-center justify-center mt-2" style="gap: 6px">
                            <span style="font-size: 11px; color: #9E9E9E">Powered by</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="17" viewBox="0 0 60 25" fill="#635BFF">
                                <path d="M5 10.2c0-.7.6-1 1.5-1 1.3 0 3 .4 4.3 1.1V6.7c-1.4-.6-2.9-.8-4.3-.8C3.2 5.9.8 7.8.8 10.4c0 4.1 5.6 3.4 5.6 5.2 0 .9-.8 1.1-1.8 1.1-1.6 0-3.6-.6-5.2-1.5v3.6c1.8.8 3.5 1.1 5.2 1.1 3.4 0 5.8-1.7 5.8-4.4-.1-4.3-5.6-3.6-5.6-5.3zM14.4 3.8l-4 .9v3.7l4-.9V3.8zM10.4 9.6h4v10.2h-4V9.6zM19.2 9.6l-.3-1h-3.5v10.2h4v-6.9c.9-1.2 2.5-1 3-.8V7.6c-.5-.2-2.5-.6-3.2 2zM24.1 5.9l-4 .8v9.3c0 1.7 1.3 3 3 3 1 0 1.7-.2 2.1-.4v-3.1c-.4.1-2.2.6-2.2-1V12h2.2V9.6h-2.2V5.9zM32.8 12.5c0-.5.4-.7.9-.7.8 0 1.8.3 2.6.7V9.7c-.9-.4-1.7-.5-2.6-.5-2.1 0-3.6 1.1-3.6 3 0 2.9 4 2.5 4 3.7 0 .6-.5.8-1.2.8-1 0-2.3-.4-3.3-1v2.7c1.1.5 2.3.7 3.3.7 2.2 0 3.7-1.1 3.7-3 0-3.2-4-2.6-4-3.8l.2.2z"/>
                            </svg>
                        </div>
                        <div class="d-flex justify-center mt-2" style="gap: 8px">
                            <v-icon size="20" color="blue-darken-3" title="Visa">mdi-credit-card-outline</v-icon>
                            <v-icon size="20" color="deep-orange" title="Mastercard">mdi-credit-card-outline</v-icon>
                            <v-icon size="20" color="blue" title="Amex">mdi-credit-card-outline</v-icon>
                            <v-icon size="20" color="orange-darken-1" title="Discover">mdi-credit-card-outline</v-icon>
                        </div>
                    </div>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button" @click="closeCardDialog">Close</v-btn>
                    <v-btn :disabled="!formData.valid" :loading="loading" color="primary" size="small" type="submit">
                        Add Payment Method
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import router from "@/router";
import {send} from "@/composables/apiClient";
import {loadStripe} from '@stripe/stripe-js';
import {useAuthStore} from "@/stores/authStore";
import {strOperations} from "@/composables/globals";
import {nextTick, onBeforeUnmount, onMounted, reactive, ref} from "vue";
import {useSnackbarStore} from "@/stores/snackBarStore";
import CompanySelect from "@/components/globals/company-select.vue";

const showPopup = router.currentRoute.value.query.popup == 1;
const cardBrandIcon = (brand) => {
    const icons = {visa: 'mdi-credit-card', mastercard: 'mdi-credit-card', amex: 'mdi-credit-card', discover: 'mdi-credit-card'};
    return icons[brand?.toLowerCase()] || 'mdi-credit-card-outline';
};
const cardBrandColor = (brand) => {
    const colors = {visa: 'blue-darken-2', mastercard: 'deep-orange', amex: 'blue', discover: 'orange-darken-1'};
    return colors[brand?.toLowerCase()] || 'grey';
};
const cardElement = ref();
const cards = ref([]);
const errors = ref([]);
const card = ref(null);
const {user} = useAuthStore();
const stripe = ref(null);
const companies = ref([]);
const elements = ref(null);
const loading = ref(false);
const setupIntent = ref(null);
const dialogSwitch = ref(false);
const selectedCompany = ref(null);
const {showSnackbar} = useSnackbarStore();
const formData = reactive({
    name: null,
    valid: false,
    cardValid: false,
});
const style = {
    base: {
        fontSize: '16px',
        fontFamily: "Avenir, Helvetica, Arial, sans-serif",
        '::placeholder': {
            color: '#96999C'
        }
    },
    invalid: {
        color: '#F10505',
        iconColor: '#F10505'
    }
};

onMounted(async () => {
    if (user.type === 'admin') {
        fetchCompanies();
    } else {
        fetchPMs();
    }
    stripe.value = await loadStripe(process.env.VUE_APP_STRIPE_KEY);
    showPopup ? await showCardDialog() : ''
});

const fetchCompanies = () => {
    send('get', `companies/list`)
        .then((response) => {
            companies.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Companies could not be loaded', 'error')
        })
}
const fetchPMs = () => {
    loading.value = true;
    send('get', `payments/methods`, {company: selectedCompany.value})
        .then((response) => {
            cards.value = response.data.data;
        })
        .catch((error) => {
            console.log(error)
            showSnackbar('Payment Methods could not be loaded', 'error')
        }).finally(() => {
        loading.value = false;
    })
}

// Tear the Stripe card Element down before letting go of it.
//
// card.value = null only dropped OUR reference. The Element is an iframe Stripe
// mounted into the page, and it outlives the reference: dropping it leaves the
// iframe and its listeners attached. Both dialog close buttons did not even do
// that much - they flipped dialogSwitch and nothing else - so every open/close
// cycle called elements.create('card') again and left the previous Element
// mounted. Open the dialog five times and there are five live card iframes,
// each still holding whatever was typed into it.
//
// destroy() is the documented teardown and it is what makes the unmount
// complete. It throws if the Element is already destroyed, so the guard is not
// decoration - closeCardDialog and the submit path can both reach it.
const destroyCard = () => {
    try {
        card.value?.destroy();
    } catch (e) {
        // Already destroyed, or never mounted. Nothing to release either way.
    }
    card.value = null;
    elements.value = null;
};

// Closing the dialog has to release the Element too, not just hide it.
const closeCardDialog = () => {
    destroyCard();
    dialogSwitch.value = false;
};

// And leaving the page while the dialog is open is a close as well - the
// component goes, the iframe would not.
onBeforeUnmount(destroyCard);

const showCardDialog = async () => {
    loading.value = true;
    dialogSwitch.value = true;

    // Whatever the last dialog left behind goes first. The close buttons release
    // the Element now, but this is the one line that holds regardless of how the
    // dialog was dismissed, so re-opening can never stack a second live card
    // iframe on top of a first.
    destroyCard();

    elements.value = stripe.value.elements();
    // Create card Element.
    card.value = elements.value.create('card', {hidePostalCode: true, style: style});
    // Handle real-time validation errors from the card Element.
    card.value.on('change', function (event) {
        errors.value = [];
        if (event.error) {
            errors.value.push(event.error.message);
        } else if (event.complete) {
            formData['valid'] = true;
            if (!formData['name']) {
                errors.value.push("Card holder name is required");
            }
        }
    });

    await nextTick();
    if (card.value) {
        card.value.mount(cardElement.value);
    } else {
        console.log("Card Element not loaded")
    }
    loading.value = false;
}

const createSetupIntent = async () => {
    try {
        const response = await send('post', `payments/create-setup-intent`, {company: selectedCompany.value});
        return response.data.data; // Ensure this line returns the setup intent data
    } catch (error) {
        console.log(error);
        showSnackbar('Setup Intent could not be created', 'error');
        throw error; // Ensure the error is thrown, so it can be caught in submitCard
    }
}

const submitCard = async () => {
    errors.value = []; // Clear previous errors

    if (!formData['name']) {
        errors.value.push("Card holder name is required");
        return false;
    }

    try {
        loading.value = true; // Set loading state

        if (!setupIntent.value) {
            console.log("Creating setup intent...")
            setupIntent.value = await createSetupIntent();
        }

        if (setupIntent.value) {
            await verifyCard(); // Call verifyCard after setupIntent is created/available
        }
    } catch (e) {
        errors.value.push(e.message); // Handle unexpected errors
        return false;
    } finally {
        loading.value = false; // Ensure loading state is reset
    }
};

const verifyCard = async () => {
    console.log("Verifying card...")
    // Verify Card Details with Payment Intent
    if (setupIntent.value) {
        const {setupIntent: confirmedIntent, error} = await stripe.value.confirmCardSetup(
            setupIntent.value.client_secret, {
                payment_method: {
                    card: card.value,
                    billing_details: {name: formData['name']}
                }
            });
        if (error) {
            errors.value.push(error.message);
            return false;
        } else {
            await savePaymentMethod(confirmedIntent.payment_method);
        }
    }
}
const savePaymentMethod = async (payment_method) => {
    console.log("Creating card...")
    try {
        const response = await send('post', `payments/create-method`, {
            payment_method: payment_method,
            company: selectedCompany.value,
        });
        showSnackbar(response.data.message);
    } catch (error) {
        console.error('Failed to add payment method:', error);
    } finally {
        fetchPMs();
        destroyCard();
        formData.name = null;
        loading.value = false;
        setupIntent.value = null;
        dialogSwitch.value = false;
    }
};
const setPmDefault = (card) => {
    if (confirm(`Are you sure to set card "${card.last_four}" as default?`)) {
        send('post', `payments/set-method-default/${card.id}`)
            .then((response) => {
                if (response.data.success) {
                    fetchPMs();
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${card.last_four}" card could not set as default`, 'error')
                }
            })
    }
}
const deletePm = (card) => {
    const defaultWarning = card.default ? '\n\nThis is the DEFAULT payment method. Deleting it will remove it from Stripe.' : '';
    if (confirm(`Are you sure you want to delete the **** ${card.last_four} ${strOperations().capitalize(card.brand)} card?${defaultWarning}`)) {
        send('delete', `payments/delete-method/${card.id}`)
            .then((response) => {
                if (response.data.success) {
                    cards.value.map((item, index) => {
                        if (item.id === card.id) {
                            cards.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                if (error.response.data.message) {
                    showSnackbar(error.response.data.message, 'error')
                } else {
                    showSnackbar(`"${card.last_four}" card could not be deleted`, 'error')
                }
            })
    }
}
</script>

<style scoped>
.trust-section {
    background: rgba(227, 242, 253, 0.5);
    border: 1px solid #BBDEFB;
}
</style>
